import { CheckCircle2, XCircle } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import type { IntakeLoad } from "@/features/intake/api/types"
import { BordereauBreakdown } from "@/features/intake/components/bordereau-breakdown"
import { SectionCard } from "@/features/intake/components/section-card"
import { useDecideLoad } from "@/features/intake/hooks/use-intake-mutations"

interface DecisionCardProps {
  load: IntakeLoad
  // When true, decision controls (Accept / Refuse, reason textarea)
  // are disabled. The breakdown stays visible. Used while inspecting
  // the QUALITY stage on a COMPLETED load (decision was already made
  // and locked in).
  readOnly?: boolean
}

export function DecisionCard({ load, readOnly = false }: DecisionCardProps) {
  const { t } = useTranslation("intake")
  const decide = useDecideLoad(load.id)
  const refused = load.grade === "REFUSED"
  const [reason, setReason] = useState(load.rejectionReason ?? "")

  const accept = () => decide.mutate({ decision: "ACCEPTED" })
  const reject = () =>
    decide.mutate({
      decision: "REJECTED",
      rejectionReason: reason.trim() || undefined,
    })

  return (
    <SectionCard
      kicker={t("decision.kicker")}
      title={t("decision.title")}
      subtitle={t("decision.subtitle")}
      roleBadge={t("detail.role.lab")}
    >
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <BordereauBreakdown load={load} />

        <div className="flex flex-col gap-3">
          <div
            className={
              "rounded-md border p-3 text-xs " +
              (refused
                ? "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300")
            }
          >
            {refused ? t("decision.helpRefused") : t("decision.helpAccepted")}
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
              {t("decision.rejectionReason")}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("decision.rejectionPlaceholder")}
              rows={3}
              disabled={readOnly}
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 text-red-700 hover:bg-red-50 hover:text-red-800 dark:text-red-300 dark:hover:bg-red-500/10"
              onClick={reject}
              disabled={decide.isPending || readOnly}
            >
              <XCircle className="size-4" />
              {t("decision.stampRefuse")}
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={accept}
              disabled={decide.isPending || refused || readOnly}
            >
              <CheckCircle2 className="size-4" />
              {t("decision.stampAccept")}
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  )
}
